import { get } from 'svelte/store';
import { track } from '../../../../utils/track/track';
import { filterStores } from '../../state';
import { getActiveArtistFromStores } from '../getActiveArtist/getActiveArtist';
import { getActiveFiltersFromStores } from '../getActiveFilters/getActiveFilters';
import { getSearchParamsSortObject } from '../getSearchParamsSortObject/getSearchParamsSortObject';
import { page } from '$app/state';

export const SELECT_ITEM_EVENT = 'SELECT_ITEM';

export interface SelectItemProps {
	// Stable identifier for the clicked item (code, slug or id) — whatever the
	// item's own PDP is keyed by, so clicks join back to the catalogue.
	itemId: string;
	// Zero-based index within the rendered grid. Drives the negative-affinity
	// signal: items repeatedly rendered above a click were seen and passed over.
	position: number;
	// The grid the click happened in (e.g. 'editions_gallery'), and the section
	// within a page that renders more than one list (e.g. a search page).
	listId: string;
	section?: string;
	artist?: string;
	// Event-name prefix, for grids that fire outside a filter drawer (e.g. the
	// search page) and so have no prefix in the store to read.
	trackPrefix?: string;
}

// A grid card click. Fired with the filter state the grid was under, so the
// click can be read as a choice among the alternatives actually on screen
// rather than an isolated page view.
export const trackSelectItem = ({
	itemId,
	position,
	listId,
	section,
	artist,
	trackPrefix
}: SelectItemProps) => {
	// Namespaced by the page's prefix (e.g. 'SHOP/SELECT_ITEM') to match the
	// filter, sort and load-more events fired from the same grid. A page that
	// has not opted into grid tracking has no prefix, so it fires nothing.
	const resolvedPrefix = trackPrefix || get(filterStores.trackPrefixStore);

	if (!resolvedPrefix) return;

	// Falls back to the artist facet when the item carries no artist of its own
	// (e.g. a series card), keeping `artist` populated wherever it is knowable.
	const resolvedArtist = artist || getActiveArtistFromStores();
	const filters = getActiveFiltersFromStores();
	const filterKeys = Object.keys(filters);
	// Read back from the URL rather than a store: `applyFilters` writes the sort
	// into the search params, and that is what the rendered order reflects.
	const sort = Object.values(getSearchParamsSortObject(page.url.searchParams)).join(',');

	track(`${resolvedPrefix}/${SELECT_ITEM_EVENT}`, {
		item_id: itemId,
		position,
		list_id: listId,
		...(section ? { section } : {}),
		...(resolvedArtist ? { artist: resolvedArtist } : {}),
		// Flattened as `filter_<facet>` so each facet is queryable as its own
		// column downstream, alongside the summary count/list.
		...filterKeys.reduce(
			(acc, key) => ({ ...acc, [`filter_${key}`]: filters[key] }),
			{} as Record<string, string>
		),
		filter_count: filterKeys.length,
		...(filterKeys.length ? { filters_applied: filterKeys.sort().join(',') } : {}),
		...(sort ? { sort_by: sort } : {})
	});
};
